import React from 'react';
import { useRouter } from 'next/router';
import { always } from 'ramda';

import { getAlphabetDivision, Letter } from '@components/letter';
import { eBaseUrl } from '@components/letter/letter.types';
import { Artist } from '@definitions/artists.types';
import { getFullArtistName } from '@utils/helpers';
import { renderWhenTrue } from '@utils/rendering';

import LogoSVG from '../../assets/images/logo.svg';

import {
  ArtistImage,
  ButtonLink,
  Container,
  LinkWrapper,
  Placeholder,
  StyledImageWrapper,
  Text,
} from './artistList.styles';
import { ARTIST_LIST_TEST_ID } from './artistList.constants';

export interface ArtistListProps {
  artists: Artist[];
  isFeatured?: boolean;
  isList?: boolean;
  onArtistMouseOver?: (artist: Artist) => void;
  onArtistMouseOut?: () => void;
}

export const ArtistList = ({
  artists,
  isFeatured = false,
  isList = false,
  onArtistMouseOver = () => '',
  onArtistMouseOut = () => '',
}: ArtistListProps) => {
  const router = useRouter();
  const alphabet = getAlphabetDivision(artists);

  const handleItemClick = async (artist: Artist) => {
    await router.push(`/artists/${artist.slug}`);
  };

  const renderAlphabet = renderWhenTrue(
    always(alphabet.map((letter) => <Letter key={letter.initial} letter={letter} baseUrl={eBaseUrl.ARTISTS} />))
  );

  const renderGrid = renderWhenTrue(
    always(
      artists.map((artist) => (
        <LinkWrapper key={artist.slug} onClick={() => handleItemClick(artist)}>
          {artist.image ? (
            <StyledImageWrapper>
              <ArtistImage src={artist.image.urls[0].url} />
            </StyledImageWrapper>
          ) : (
            <Placeholder>
              <LogoSVG />
            </Placeholder>
          )}
          <Text>{getFullArtistName(artist)}</Text>
        </LinkWrapper>
      ))
    )
  );

  const renderList = renderWhenTrue(
    always(
      artists.map((artist) => (
        <ButtonLink
          onMouseOver={() => onArtistMouseOver(artist)}
          onMouseOut={onArtistMouseOut}
          isCentered
          isLarge
          onClick={() => handleItemClick(artist)}
          key={artist.slug}
        >
          {getFullArtistName(artist)}
        </ButtonLink>
      ))
    )
  );

  return (
    <Container isList={isList} isFeatured={isFeatured} data-testid={ARTIST_LIST_TEST_ID}>
      {renderGrid(!isList && isFeatured)}
      {renderList(isList && isFeatured)}
      {renderAlphabet(isList && !isFeatured)}
    </Container>
  );
};
